import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useBrand } from '../../contexts/BrandContext';
import {
    Palette, Type, Image, Save, Loader2, RotateCcw, Upload, Eye, Globe, Mail, Link2, MapPin, DollarSign, Sparkles, UserPlus, AlertTriangle, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const resolveFileUrl = (url) => {
    if (!url) return url;
    const idx = url.indexOf('/api/files/');
    if (idx !== -1) return `${API}${url.substring(idx)}`;
    return url;
};
function ColorPicker({ label, value, onChange, testId }) {
    return (
        <div className="space-y-2">
            <Label className="text-sm">{label}</Label>
            <div className="flex items-center gap-3">
                <div className="relative">
                    <input
                        type="color"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-12 h-12 rounded-lg cursor-pointer border-2 border-white/10 bg-transparent"
                        data-testid={testId}
                    />
                </div>
                <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="#D4AF37"
                    className="bg-[#0A0D14] border-white/10 font-mono text-sm w-32"
                />
            </div>
        </div>
    );
}

export default function AdminSettings() {
    const brand = useBrand();
    const [form, setForm] = useState({
        platform_name: 'Voxoora',
        tagline: 'Immersive Audio Experiences',
        logo_url: '',
        favicon_url: '',
        primary_color: '#D4AF37',
        secondary_color: '#0A0D14',
        accent_color: '#1A1F2E',
        text_color: '#FFFFFF',
        font_family: 'Outfit',
        footer_text: '',
        support_email: '',
        support_url: ''
    });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [logoUploading, setLogoUploading] = useState(false);
    const [gps, setGps] = useState({ enabled: false, default_trigger_radius: 50, auto_play: false, show_map: true });
    const [gpsSaving, setGpsSaving] = useState(false);
    const [pricing, setPricing] = useState({ standard_credit_rate: 1.0, whitelabel_credit_rate: 0.25, direct_traveler_rate: 1.0 });
    const [pricingSaving, setPricingSaving] = useState(false);
    const [features, setFeatures] = useState({ ar_enabled: false, ai_enabled: false });
    const [featuresSaving, setFeaturesSaving] = useState(false);
    const [regConfig, setRegConfig] = useState({ registration_enabled: true, auto_approve: false });
    const [regSaving, setRegSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [resetConfirm, setResetConfirm] = useState('');
    const [resetResult, setResetResult] = useState(null);

    useEffect(() => {
        fetchBrand();
        fetchGps();
        fetchPricing();
        fetchFeatures();
        fetchRegConfig();
    }, []);

    const fetchBrand = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/brand`, { withCredentials: true });
            const data = { ...res.data };
            data.logo_url = resolveFileUrl(data.logo_url);
            data.favicon_url = resolveFileUrl(data.favicon_url);
            setForm(prev => ({ ...prev, ...data }));
        } catch (e) {
            // Use defaults
        } finally {
            setLoading(false);
        }
    };

    const fetchGps = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/gps-settings`, { withCredentials: true });
            setGps(prev => ({ ...prev, ...res.data }));
        } catch (e) {
            console.error('Failed to fetch GPS settings:', e);
        }
    };

    const saveGps = async () => {
        setGpsSaving(true);
        try {
            await axios.put(`${API}/api/admin/gps-settings`, gps, { withCredentials: true });
            toast.success('GPS settings saved');
        } catch (e) {
            toast.error('Failed to save GPS settings');
        } finally {
            setGpsSaving(false);
        }
    };

    const fetchPricing = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/settings/pricing`, { withCredentials: true });
            setPricing(prev => ({ ...prev, ...res.data }));
        } catch (e) {
            console.error('Failed to fetch pricing:', e);
        }
    };

    const savePricing = async () => {
        setPricingSaving(true);
        try {
            await axios.put(`${API}/api/admin/settings/pricing`, pricing, { withCredentials: true });
            toast.success('Pricing settings saved');
        } catch (e) {
            toast.error('Failed to save pricing settings');
        } finally {
            setPricingSaving(false);
        }
    };

    const fetchFeatures = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/settings/features`, { withCredentials: true });
            setFeatures(prev => ({ ...prev, ...res.data }));
        } catch (e) {
            console.error('Failed to fetch features:', e);
        }
    };

    const saveFeatures = async () => {
        setFeaturesSaving(true);
        try {
            await axios.put(`${API}/api/admin/settings/features`, features, { withCredentials: true });
            toast.success('Feature settings saved');
        } catch (e) {
            toast.error('Failed to save feature settings');
        } finally {
            setFeaturesSaving(false);
        }
    };

    const fetchRegConfig = async () => {
        try {
            const res = await axios.get(`${API}/api/admin/settings/registration`, { withCredentials: true });
            setRegConfig(prev => ({ ...prev, ...res.data }));
        } catch (e) {
            console.error('Failed to fetch registration config:', e);
        }
    };

    const saveRegConfig = async () => {
        setRegSaving(true);
        try {
            await axios.put(`${API}/api/admin/settings/registration`, regConfig, { withCredentials: true });
            toast.success('Registration settings saved');
        } catch (e) {
            toast.error('Failed to save registration settings');
        } finally {
            setRegSaving(false);
        }
    };

    const handlePlatformReset = async () => {
        if (resetConfirm !== 'RESET') {
            toast.error('Please type RESET to confirm');
            return;
        }
        setResetting(true);
        setResetResult(null);
        try {
            const res = await axios.post(`${API}/api/admin/platform-reset`, {}, { withCredentials: true });
            setResetResult(res.data);
            setResetConfirm('');
            toast.success('Platform data reset complete');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to reset platform data');
        } finally {
            setResetting(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/api/admin/brand`, form, { withCredentials: true });
            brand.refreshBrand();
            toast.success('Brand settings saved. Changes are live.');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (file) => {
        if (!file) return;
        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await axios.post(`${API}/api/upload`, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const url = `${API}/api/files/${res.data.path}`;
            setForm(prev => ({ ...prev, logo_url: url }));
            toast.success('Logo uploaded');
        } catch (e) {
            toast.error('Upload failed');
        } finally {
            setLogoUploading(false);
        }
    };

    const resetDefaults = () => {
        setForm({
            platform_name: 'Voxoora',
            tagline: 'Immersive Audio Experiences',
            logo_url: '',
            favicon_url: '',
            primary_color: '#D4AF37',
            secondary_color: '#0A0D14',
            accent_color: '#1A1F2E',
            text_color: '#FFFFFF',
            font_family: 'Outfit',
            footer_text: '',
            support_email: '',
            support_url: ''
        });
        toast.info('Reset to defaults. Click Save to apply.');
    };

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-settings">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Platform Settings</h1>
                        <p className="text-muted-foreground mt-1">Control your brand identity, colors, and platform appearance</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={resetDefaults} className="border-white/10 gap-2" data-testid="reset-defaults-btn">
                            <RotateCcw className="w-4 h-4" /> Reset Defaults
                        </Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-brand-btn">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            Save Changes
                        </Button>
                    </div>
                </div>

                <Tabs defaultValue="brand" className="space-y-6">
                    <TabsList className="bg-[#121620] border border-white/10">
                        <TabsTrigger value="brand" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2">
                            <Type className="w-4 h-4" /> Brand
                        </TabsTrigger>
                        <TabsTrigger value="colors" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2">
                            <Palette className="w-4 h-4" /> Colors
                        </TabsTrigger>
                        <TabsTrigger value="logo" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2">
                            <Image className="w-4 h-4" /> Logo & Media
                        </TabsTrigger>
                        <TabsTrigger value="general" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2">
                            <Globe className="w-4 h-4" /> General
                        </TabsTrigger>
                        <TabsTrigger value="gps" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2" data-testid="gps-tab">
                            <MapPin className="w-4 h-4" /> GPS Audio
                        </TabsTrigger>
                        <TabsTrigger value="pricing" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2" data-testid="pricing-tab">
                            <DollarSign className="w-4 h-4" /> Pricing
                        </TabsTrigger>
                        <TabsTrigger value="features" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2" data-testid="features-tab">
                            <Sparkles className="w-4 h-4" /> AR & AI
                        </TabsTrigger>
                        <TabsTrigger value="registration" className="data-[state=active]:bg-gold data-[state=active]:text-[#0A0D14] gap-2" data-testid="registration-tab">
                            <UserPlus className="w-4 h-4" /> Registration
                        </TabsTrigger>
                        <TabsTrigger value="danger" className="data-[state=active]:bg-red-600 data-[state=active]:text-white gap-2" data-testid="danger-zone-tab">
                            <AlertTriangle className="w-4 h-4" /> Danger Zone
                        </TabsTrigger>
                    </TabsList>

                    {/* ===== BRAND TAB ===== */}
                    <TabsContent value="brand">
                        <div className="grid lg:grid-cols-2 gap-6">
                            <Card className="bg-[#121620] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white font-outfit text-lg">Brand Identity</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="space-y-2">
                                        <Label>Platform Name</Label>
                                        <Input
                                            value={form.platform_name}
                                            onChange={(e) => setForm({ ...form, platform_name: e.target.value })}
                                            placeholder="Voxoora"
                                            className="bg-[#0A0D14] border-white/10 text-lg font-outfit"
                                            data-testid="brand-name-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Displayed across the entire platform — sidebar, login, landing page</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tagline</Label>
                                        <Input
                                            value={form.tagline}
                                            onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                                            placeholder="Immersive Audio Experiences"
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="brand-tagline-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Font Family</Label>
                                        <Input
                                            value={form.font_family}
                                            onChange={(e) => setForm({ ...form, font_family: e.target.value })}
                                            placeholder="Outfit"
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                        <p className="text-xs text-muted-foreground">Google Font name (must be loaded in CSS)</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Footer Text</Label>
                                        <Input
                                            value={form.footer_text}
                                            onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                                            placeholder={`© 2026 ${form.platform_name}. All rights reserved.`}
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="brand-footer-input"
                                        />
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Live Preview */}
                            <Card className="bg-[#121620] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white font-outfit text-lg flex items-center gap-2">
                                        <Eye className="w-4 h-4 text-gold" /> Live Preview
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div
                                        className="rounded-xl overflow-hidden border border-white/10"
                                        style={{ backgroundColor: form.secondary_color }}
                                    >
                                        {/* Preview Sidebar */}
                                        <div className="p-4 flex items-center gap-3 border-b border-white/10">
                                            {form.logo_url ? (
                                                <img src={form.logo_url} alt="Logo" className="w-10 h-10 rounded-md object-contain" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ backgroundColor: form.primary_color }}>
                                                    <Type className="w-5 h-5" style={{ color: form.secondary_color }} />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-semibold" style={{ color: form.text_color, fontFamily: form.font_family }}>
                                                    {form.platform_name || 'Voxoora'}
                                                </p>
                                                <p className="text-xs opacity-50" style={{ color: form.text_color }}>
                                                    {form.tagline || 'Your tagline'}
                                                </p>
                                            </div>
                                        </div>
                                        {/* Preview Content */}
                                        <div className="p-6 space-y-3">
                                            <div className="h-3 rounded-full w-3/4" style={{ backgroundColor: form.accent_color }} />
                                            <div className="h-3 rounded-full w-1/2" style={{ backgroundColor: form.accent_color }} />
                                            <div className="mt-4 flex gap-3">
                                                <div className="px-4 py-2 rounded-md text-sm font-medium" style={{ backgroundColor: form.primary_color, color: form.secondary_color }}>
                                                    Primary Button
                                                </div>
                                                <div className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: form.primary_color + '40', color: form.primary_color }}>
                                                    Secondary
                                                </div>
                                            </div>
                                        </div>
                                        {/* Preview Footer */}
                                        <div className="p-3 text-center text-xs opacity-40 border-t border-white/10" style={{ color: form.text_color }}>
                                            {form.footer_text || `© 2026 ${form.platform_name}`}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* ===== COLORS TAB ===== */}
                    <TabsContent value="colors">
                        <div className="grid lg:grid-cols-2 gap-6">
                            <Card className="bg-[#121620] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white font-outfit text-lg">Color Palette</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <ColorPicker
                                        label="Primary Color (Gold / Accent)"
                                        value={form.primary_color}
                                        onChange={(v) => setForm({ ...form, primary_color: v })}
                                        testId="color-primary"
                                    />
                                    <ColorPicker
                                        label="Background Color (Dark Base)"
                                        value={form.secondary_color}
                                        onChange={(v) => setForm({ ...form, secondary_color: v })}
                                        testId="color-secondary"
                                    />
                                    <ColorPicker
                                        label="Accent Color (Cards, Panels)"
                                        value={form.accent_color}
                                        onChange={(v) => setForm({ ...form, accent_color: v })}
                                        testId="color-accent"
                                    />
                                    <ColorPicker
                                        label="Text Color"
                                        value={form.text_color}
                                        onChange={(v) => setForm({ ...form, text_color: v })}
                                        testId="color-text"
                                    />
                                </CardContent>
                            </Card>

                            {/* Color Swatches Preview */}
                            <Card className="bg-[#121620] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white font-outfit text-lg">Color Preview</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        {[
                                            { label: 'Primary', color: form.primary_color },
                                            { label: 'Background', color: form.secondary_color },
                                            { label: 'Accent', color: form.accent_color },
                                            { label: 'Text', color: form.text_color }
                                        ].map((s, i) => (
                                            <div key={i} className="space-y-2">
                                                <div
                                                    className="w-full h-20 rounded-lg border border-white/10"
                                                    style={{ backgroundColor: s.color }}
                                                />
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs text-muted-foreground">{s.label}</span>
                                                    <code className="text-xs text-white font-mono">{s.color}</code>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Combination Preview */}
                                    <div className="rounded-lg overflow-hidden border border-white/10 mt-4" style={{ backgroundColor: form.secondary_color }}>
                                        <div className="p-4">
                                            <h3 className="text-lg font-semibold mb-2" style={{ color: form.text_color, fontFamily: form.font_family }}>
                                                Sample Heading
                                            </h3>
                                            <p className="text-sm mb-3" style={{ color: form.text_color, opacity: 0.6 }}>
                                                This is how your content will look with the selected colors.
                                            </p>
                                            <div className="p-3 rounded-md" style={{ backgroundColor: form.accent_color }}>
                                                <p className="text-sm" style={{ color: form.primary_color }}>Highlighted content area</p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* ===== LOGO TAB ===== */}
                    <TabsContent value="logo">
                        <div className="grid lg:grid-cols-2 gap-6">
                            <Card className="bg-[#121620] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white font-outfit text-lg">Logo</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    {/* Upload or URL */}
                                    <div className="space-y-3">
                                        <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-gold/30 transition-colors">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                                                className="hidden"
                                                id="logo-upload"
                                            />
                                            <label htmlFor="logo-upload" className="cursor-pointer">
                                                {logoUploading ? (
                                                    <Loader2 className="w-8 h-8 mx-auto mb-2 text-gold animate-spin" />
                                                ) : (
                                                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                                                )}
                                                <p className="text-sm text-muted-foreground">
                                                    {logoUploading ? 'Uploading...' : 'Click to upload logo'}
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-1">PNG, SVG, or JPG (recommended: 200x200px)</p>
                                            </label>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-px bg-white/10" />
                                            <span className="text-xs text-muted-foreground">or paste URL</span>
                                            <div className="flex-1 h-px bg-white/10" />
                                        </div>
                                        <Input
                                            value={form.logo_url}
                                            onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                                            placeholder="https://example.com/logo.png"
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="logo-url-input"
                                        />
                                    </div>

                                    {/* Favicon */}
                                    <div className="space-y-2 pt-4 border-t border-white/10">
                                        <Label>Favicon URL</Label>
                                        <Input
                                            value={form.favicon_url}
                                            onChange={(e) => setForm({ ...form, favicon_url: e.target.value })}
                                            placeholder="https://example.com/favicon.ico"
                                            className="bg-[#0A0D14] border-white/10"
                                        />
                                        <p className="text-xs text-muted-foreground">Browser tab icon (ICO or PNG, 32x32px)</p>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Logo Preview */}
                            <Card className="bg-[#121620] border-white/10">
                                <CardHeader>
                                    <CardTitle className="text-white font-outfit text-lg">Logo Preview</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {form.logo_url ? (
                                        <div className="space-y-4">
                                            {/* Light bg */}
                                            <div className="p-6 bg-white rounded-lg flex items-center justify-center">
                                                <img src={form.logo_url} alt="Logo light" className="max-w-[200px] max-h-[80px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                            </div>
                                            {/* Dark bg */}
                                            <div className="p-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: form.secondary_color }}>
                                                <img src={form.logo_url} alt="Logo dark" className="max-w-[200px] max-h-[80px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                            </div>
                                            {/* Sidebar mockup */}
                                            <div className="p-4 rounded-lg border border-white/10 flex items-center gap-3" style={{ backgroundColor: form.secondary_color }}>
                                                <img src={form.logo_url} alt="Logo" className="w-10 h-10 rounded-md object-contain" />
                                                <div>
                                                    <p className="font-semibold text-white" style={{ fontFamily: form.font_family }}>{form.platform_name}</p>
                                                    <p className="text-xs text-muted-foreground">Admin Panel</p>
                                                </div>
                                            </div>
                                            <Button variant="ghost" onClick={() => setForm({ ...form, logo_url: '' })} className="text-red-400 hover:text-red-300 text-xs">
                                                Remove Logo
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="text-center py-12 text-muted-foreground">
                                            <Image className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                            <p className="text-sm">No logo uploaded yet</p>
                                            <p className="text-xs mt-1">A default icon will be shown</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* ===== GENERAL TAB ===== */}
                    <TabsContent value="general">
                        <Card className="bg-[#121620] border-white/10 max-w-xl">
                            <CardHeader>
                                <CardTitle className="text-white font-outfit text-lg">Support & Contact</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-5">
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2"><Mail className="w-3 h-3" /> Support Email</Label>
                                    <Input
                                        type="email"
                                        value={form.support_email}
                                        onChange={(e) => setForm({ ...form, support_email: e.target.value })}
                                        placeholder="support@voxoora.com"
                                        className="bg-[#0A0D14] border-white/10"
                                        data-testid="support-email-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2"><Link2 className="w-3 h-3" /> Support URL</Label>
                                    <Input
                                        value={form.support_url}
                                        onChange={(e) => setForm({ ...form, support_url: e.target.value })}
                                        placeholder="https://help.voxoora.com"
                                        className="bg-[#0A0D14] border-white/10"
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ===== GPS TAB ===== */}
                    <TabsContent value="gps">
                        <Card className="bg-[#121620] border-white/10">
                            <CardHeader>
                                <CardTitle className="text-white font-outfit text-lg flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-gold" /> GPS-Triggered Audio
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <p className="text-sm text-muted-foreground">
                                    When enabled, travelers will see an interactive map and receive proximity notifications when near audio points of interest.
                                </p>

                                {/* Enable/Disable */}
                                <div className="flex items-center justify-between p-4 rounded-xl bg-[#0A0D14] border border-white/10">
                                    <div>
                                        <p className="text-white font-medium">Enable GPS Audio</p>
                                        <p className="text-sm text-muted-foreground">Show map and proximity triggers for travelers</p>
                                    </div>
                                    <button
                                        onClick={() => setGps(prev => ({ ...prev, enabled: !prev.enabled }))}
                                        className={`w-12 h-6 rounded-full transition-colors relative ${gps.enabled ? 'bg-gold' : 'bg-white/20'}`}
                                        data-testid="gps-toggle"
                                    >
                                        <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${gps.enabled ? 'left-6' : 'left-0.5'}`} />
                                    </button>
                                </div>

                                {gps.enabled && (
                                    <div className="space-y-4 animate-fade-in">
                                        {/* Default Trigger Radius */}
                                        <div className="space-y-2">
                                            <Label>Default Trigger Radius (meters)</Label>
                                            <Input
                                                type="number"
                                                min={10}
                                                max={500}
                                                value={gps.default_trigger_radius}
                                                onChange={(e) => setGps(prev => ({ ...prev, default_trigger_radius: parseInt(e.target.value) || 50 }))}
                                                className="bg-[#0A0D14] border-white/10 w-40"
                                                data-testid="gps-radius-input"
                                            />
                                            <p className="text-xs text-muted-foreground">How close (in meters) a traveler must be to trigger the audio notification</p>
                                        </div>

                                        {/* Show Map */}
                                        <div className="flex items-center justify-between p-4 rounded-xl bg-[#0A0D14] border border-white/10">
                                            <div>
                                                <p className="text-white font-medium">Show Interactive Map</p>
                                                <p className="text-sm text-muted-foreground">Display a map with POI pins in the traveler guide</p>
                                            </div>
                                            <button
                                                onClick={() => setGps(prev => ({ ...prev, show_map: !prev.show_map }))}
                                                className={`w-12 h-6 rounded-full transition-colors relative ${gps.show_map ? 'bg-gold' : 'bg-white/20'}`}
                                                data-testid="gps-map-toggle"
                                            >
                                                <div className={`w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all ${gps.show_map ? 'left-6' : 'left-0.5'}`} />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <Button onClick={saveGps} disabled={gpsSaving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-gps-btn">
                                    {gpsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save GPS Settings
                                </Button>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ===== PRICING TAB ===== */}
                    <TabsContent value="pricing">
                        <Card className="bg-[#121620] border-white/10">
                            <CardHeader>
                                <CardTitle className="text-white font-outfit flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-gold" />
                                    Credit Pricing
                                </CardTitle>
                                <p className="text-sm text-muted-foreground">Configure credit rates for different operator types. 1 credit unit = 1 attraction on 1 device.</p>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid md:grid-cols-3 gap-6">
                                    <div className="space-y-2">
                                        <Label>Standard Operator Rate</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min={0.01}
                                            value={pricing.standard_credit_rate}
                                            onChange={(e) => setPricing(prev => ({ ...prev, standard_credit_rate: parseFloat(e.target.value) || 1 }))}
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="standard-rate-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Credits per attraction per device for standard operators</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>White-Label Rate</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min={0.01}
                                            value={pricing.whitelabel_credit_rate}
                                            onChange={(e) => setPricing(prev => ({ ...prev, whitelabel_credit_rate: parseFloat(e.target.value) || 0.25 }))}
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="whitelabel-rate-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Discounted rate for white-label operators</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Direct Traveler Rate</Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min={0.01}
                                            value={pricing.direct_traveler_rate}
                                            onChange={(e) => setPricing(prev => ({ ...prev, direct_traveler_rate: parseFloat(e.target.value) || 1 }))}
                                            className="bg-[#0A0D14] border-white/10"
                                            data-testid="traveler-rate-input"
                                        />
                                        <p className="text-xs text-muted-foreground">Cost per attraction per device for direct Voxoora travelers</p>
                                    </div>
                                </div>
                                <div className="p-4 rounded-xl bg-[#0A0D14] border border-white/10">
                                    <p className="text-sm font-medium text-white mb-2">Cost Formula</p>
                                    <p className="text-xs text-muted-foreground">Total cost = Quantity × Attractions × Devices × Rate</p>
                                    <div className="grid grid-cols-3 gap-4 mt-3 text-center">
                                        <div className="p-2 rounded-lg bg-white/5">
                                            <p className="text-gold font-semibold">{pricing.standard_credit_rate}</p>
                                            <p className="text-[10px] text-muted-foreground">Standard</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-white/5">
                                            <p className="text-gold font-semibold">{pricing.whitelabel_credit_rate}</p>
                                            <p className="text-[10px] text-muted-foreground">White-Label</p>
                                        </div>
                                        <div className="p-2 rounded-lg bg-white/5">
                                            <p className="text-gold font-semibold">{pricing.direct_traveler_rate}</p>
                                            <p className="text-[10px] text-muted-foreground">Direct Traveler</p>
                                        </div>
                                    </div>
                                </div>
                                <Button onClick={savePricing} disabled={pricingSaving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-pricing-btn">
                                    {pricingSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Pricing
                                </Button>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ===== FEATURES TAB ===== */}
                    <TabsContent value="features">
                        <Card className="bg-[#121620] border-white/10">
                            <CardHeader>
                                <CardTitle className="text-white font-outfit text-lg">AR & AI Features</CardTitle>
                                <p className="text-sm text-white/50">Enable or disable AR and AI features globally. Once enabled, assign them to specific operators.</p>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="flex items-center justify-between p-4 bg-[#0A0D14] rounded-lg border border-white/5">
                                    <div>
                                        <p className="text-white font-medium">AR Experience</p>
                                        <p className="text-sm text-white/40">Historical overlays, reconstruction images, and info popups at attractions</p>
                                    </div>
                                    <button
                                        onClick={() => setFeatures(f => ({ ...f, ar_enabled: !f.ar_enabled }))}
                                        className={`relative w-12 h-6 rounded-full transition-colors ${features.ar_enabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                                        data-testid="ar-toggle"
                                    >
                                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${features.ar_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-[#0A0D14] rounded-lg border border-white/5">
                                    <div>
                                        <p className="text-white font-medium">AI Assistant</p>
                                        <p className="text-sm text-white/40">GPT-5.2 powered chatbot for traveler questions about attractions</p>
                                    </div>
                                    <button
                                        onClick={() => setFeatures(f => ({ ...f, ai_enabled: !f.ai_enabled }))}
                                        className={`relative w-12 h-6 rounded-full transition-colors ${features.ai_enabled ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                                        data-testid="ai-toggle"
                                    >
                                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${features.ai_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>
                                <Button onClick={saveFeatures} disabled={featuresSaving} className="bg-gold hover:bg-gold/90 text-[#0A0D14] font-semibold" data-testid="save-features-btn">
                                    {featuresSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                                    Save Feature Settings
                                </Button>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ===== REGISTRATION TAB ===== */}
                    <TabsContent value="registration">
                        <Card className="bg-[#121620] border-white/10">
                            <CardHeader>
                                <CardTitle className="text-white font-outfit flex items-center gap-2">
                                    <UserPlus className="w-5 h-5 text-gold" />
                                    Operator Registration Settings
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                                        <div>
                                            <p className="text-white font-medium">Public Registration</p>
                                            <p className="text-sm text-muted-foreground mt-1">Allow operators (Tour Operators, DMCs, OTAs, etc.) to self-register via the registration page</p>
                                        </div>
                                        <button
                                            onClick={() => setRegConfig(p => ({...p, registration_enabled: !p.registration_enabled}))}
                                            className={`w-12 h-6 rounded-full transition-all ${regConfig.registration_enabled ? 'bg-gold' : 'bg-white/20'}`}
                                            data-testid="toggle-registration"
                                        >
                                            <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${regConfig.registration_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                                        <div>
                                            <p className="text-white font-medium">Auto-Approve</p>
                                            <p className="text-sm text-muted-foreground mt-1">Automatically approve new operator registrations (if off, admin must manually approve each registration)</p>
                                        </div>
                                        <button
                                            onClick={() => setRegConfig(p => ({...p, auto_approve: !p.auto_approve}))}
                                            className={`w-12 h-6 rounded-full transition-all ${regConfig.auto_approve ? 'bg-gold' : 'bg-white/20'}`}
                                            data-testid="toggle-auto-approve"
                                        >
                                            <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform ${regConfig.auto_approve ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                        </button>
                                    </div>
                                </div>

                                <div className="p-4 rounded-lg bg-gold/5 border border-gold/10">
                                    <p className="text-sm text-gold font-medium mb-1">Registration URL</p>
                                    <p className="text-xs text-muted-foreground font-mono">{window.location.origin}/register/operator</p>
                                </div>

                                <Button onClick={saveRegConfig} disabled={regSaving} className="bg-gold text-[#0A0D14] hover:bg-gold/90 gap-2" data-testid="save-reg-settings">
                                    {regSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    Save Registration Settings
                                </Button>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ===== DANGER ZONE TAB ===== */}
                    <TabsContent value="danger">
                        <Card className="bg-[#121620] border-red-500/30">
                            <CardHeader>
                                <CardTitle className="text-red-400 font-outfit flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5" />
                                    Platform Data Reset
                                </CardTitle>
                                <p className="text-sm text-white/50">
                                    This action will permanently delete all transactional data including orders, users (except admin), chats, redemptions, analytics, blog posts, and discount codes.
                                </p>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 space-y-3">
                                    <p className="text-sm text-white font-medium">The following will be preserved:</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {['Cities', 'Attractions', 'Listings', 'Audio Content', 'Platform Settings', 'Admin Users'].map(item => (
                                            <div key={item} className="flex items-center gap-2 text-sm text-emerald-400">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20 space-y-3">
                                    <p className="text-sm text-white font-medium">The following will be deleted:</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {['Orders', 'Non-admin Users', 'Chat Sessions', 'Notifications', 'Operators', 'Redemptions', 'Analytics Data', 'Blog Posts', 'Discount Codes'].map(item => (
                                            <div key={item} className="flex items-center gap-2 text-sm text-red-400">
                                                <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-3 pt-2">
                                    <Label className="text-white text-sm">Type <span className="font-mono text-red-400 font-bold">RESET</span> to confirm</Label>
                                    <div className="flex items-center gap-3">
                                        <Input
                                            value={resetConfirm}
                                            onChange={(e) => setResetConfirm(e.target.value)}
                                            placeholder="Type RESET to confirm"
                                            className="bg-[#0A0D14] border-red-500/30 text-white font-mono max-w-xs"
                                            data-testid="reset-confirm-input"
                                        />
                                        <Button
                                            onClick={handlePlatformReset}
                                            disabled={resetting || resetConfirm !== 'RESET'}
                                            className="bg-red-600 hover:bg-red-700 text-white gap-2 disabled:opacity-30"
                                            data-testid="reset-platform-btn"
                                        >
                                            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                            Reset Platform Data
                                        </Button>
                                    </div>
                                </div>

                                {resetResult && (
                                    <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 space-y-3 animate-fade-in" data-testid="reset-result">
                                        <p className="text-emerald-400 font-medium text-sm flex items-center gap-2">
                                            <Save className="w-4 h-4" /> Reset Complete
                                        </p>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                            {Object.entries(resetResult.remaining || {}).map(([key, val]) => (
                                                <div key={key} className="p-2 rounded-lg bg-white/5">
                                                    <p className="text-gold font-semibold">{val}</p>
                                                    <p className="text-[10px] text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
